import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowRight,
  Download,
  Eye,
  Grid2X2,
  List,
  NotebookPen,
  Plus,
  UserCheck,
  Upload as UploadIcon,
} from "lucide-react";
import { brokersApi } from "@/api/brokers";
import { calendarDaysUntil, isoDateInTz } from "@/utils/datetime";
import type { BrokerStatus, BrokerSummaryDto } from "@/api/brokers";
import { AssignToTeamModal } from "./AssignToTeamModal";
import { Badge, Button, EmptyState, Input } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import {
  BulkActionBar,
  MyToggle,
  RowActions,
  SavedFilterBar,
  StarButton,
  type BulkAction,
  type RowAction,
} from "@/components/ux";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useWatchlistStatuses } from "@/hooks/useWatchlistStatuses";
import { cn } from "@/design-system/lib/utils";
import { useDateFormatter } from "@/utils/datetime";

// Broker Status Refactor: Kanban shows ONLY the active pipeline. Archived
// brokers (Inactive / Suspended / Disqualified / Declined) live under the
// dedicated Archived tab on the table view.
const PIPELINE_ORDER: BrokerStatus[] = ["Prospect", "Qualified", "Onboarding", "Certified"];

const STATUS_BADGE: Record<BrokerStatus, "gold" | "info" | "finalized" | "draft" | "review" | "archived" | "error"> = {
  Prospect: "info",
  Qualified: "gold",
  Onboarding: "review",
  Certified: "finalized",
  Inactive: "archived",
  Suspended: "review",
  Disqualified: "error",
  Declined: "archived",
};

// Shared expiration chip so the Kanban cards and the table rows render the
// same wording/colour. Today reads "Expires Today"; a past date reads
// "Expired" in the error (red) colour rather than a negative day count.
function ExpirationBadge({ days, className }: { days: number; className?: string }) {
  if (days < 0) {
    return <Badge status="error" className={className}>Expired</Badge>;
  }
  return (
    <Badge status="review" className={className}>
      {days === 0 ? "Expires Today" : `Expires ${days}d`}
    </Badge>
  );
}

// Whole-day difference between today and an ISO date-only string (the format
// EoExpirationDate / LicenseExpirationDate serialize to). Compared in UTC so
// DST transitions don't shift the count.
// The nearest-to-correct of the rivals this replaced: it was already
// midnight-anchored and already read today as a CALENDAR day. Its one gap was
// reading that day from the BROWSER's zone (`now.getFullYear()` and friends)
// rather than the zone the app resolves for the user, so a traveller — or
// anyone whose profile timezone differs from their machine's — got a different
// answer from the rest of the product for the same date.
//
// That convergence moved the RULE and not the ZONE: the replacement passed no
// `tz`, so `calendarDaysUntil` fell back to `America/Phoenix` and the comment
// above described an outcome the code did not produce. `tz` is a parameter now
// because this is a module-level function and cannot read the context itself —
// its caller holds the zone.
function daysUntilDate(iso: string | null | undefined): number | null {
  return calendarDaysUntil(iso);
}

// Per-type expiration cell for the table: the expiration chip when within
// the alert window, otherwise a green "OK" chip.
function ExpirationCell({ alerting, days }: { alerting: boolean; days: number | null }) {
  if (alerting && days !== null) {
    return <ExpirationBadge days={days} />;
  }
  return <Badge status="finalized">OK</Badge>;
}

interface BrokerTableState {
  search: string;
  onlyAlerts: boolean;
  assignedToMe: boolean;
}

const DEFAULT_STATE: BrokerTableState = {
  search: "",
  onlyAlerts: false,
  assignedToMe: false,
};

const PAGE_KEY = "brokers";
const PAGE_SIZE = 25;

export function BrokersPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const canCreate = hasPermission("Brokers.Create");
  const canUpdate = hasPermission("Brokers.Update");
  const canQualify = hasPermission("Brokers.Qualify");
  const { date, tz } = useDateFormatter();

  const [view, setView] = useState<"kanban" | "table">("kanban");
  // Broker Status Refactor: tab only applies to the table view. Kanban
  // is always the active pipeline (§7). "archive" renders archived
  // brokers with reactivate affordances (§8).
  const [tableTab, setTableTab] = useState<"pipeline" | "archive">("pipeline");
  const [state, setState] = useState<BrokerTableState>(DEFAULT_STATE);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showLogActivity, setShowLogActivity] = useState<BrokerSummaryDto | null>(null);
  // WS6 graduation — the broker being assigned to a team from the kanban's
  // "Ready to graduate" affordance.
  const [graduating, setGraduating] = useState<BrokerSummaryDto | null>(null);

  const handleMine = useCallback((mine: boolean) => {
    setState((s) => ({ ...s, assignedToMe: mine }));
  }, []);

  // Reset to page 1 whenever the filter set or tab changes so we don't land
  // on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [state, tableTab]);

  const pipeline = useQuery({
    queryKey: brokerKeys.pipeline(state.assignedToMe),
    queryFn: () => brokersApi.pipeline(state.assignedToMe),
    enabled: view === "kanban",
  });
  const table = useQuery({
    queryKey: brokerKeys.table(state, tableTab, page),
    queryFn: () =>
      brokersApi.list({
        page,
        pageSize: PAGE_SIZE,
        q: state.search || undefined,
        onlyAlerts: state.onlyAlerts,
        view: tableTab === "archive" ? "Archive" : "Pipeline",
        assignedToMe: state.assignedToMe,
      }),
    enabled: view === "table",
  });
  const alerts = useQuery({
    queryKey: brokerKeys.alerts(),
    queryFn: () => brokersApi.alerts(),
  });

  // One batch star-status call for every visible row/card — each StarButton
  // gets a prop-fed status instead of firing its own GET per row.
  const visibleBrokerIds = useMemo(() => {
    if (view === "table") return (table.data?.data ?? []).map((b) => b.id);
    const stages = pipeline.data?.stages;
    return stages ? Object.values(stages).flat().map((b) => b.id) : [];
  }, [view, table.data, pipeline.data]);
  const watchStatuses = useWatchlistStatuses("Broker", visibleBrokerIds);

  const bulkStatus = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: BrokerStatus }) =>
      brokersApi.bulkStatus(ids, status, null),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: brokerKeys.lists() });
      setSelectedIds([]);
      void dialogs.alert({
        title: "Bulk status change",
        tone: result.failureCount > 0 ? "error" : "success",
        message: (
          <>
            <p>
              {result.successCount} succeeded, {result.failureCount} failed.
            </p>
            {result.failures.length > 0 && (
              <ul className="mt-space-2 font-mono text-caption list-disc pl-space-4">
                {result.failures.map((f) => (
                  <li key={f.brokerId}>
                    <span className="text-gray-mid">{f.brokerId.slice(0, 8)}</span>: {f.error}
                  </li>
                ))}
              </ul>
            )}
          </>
        ),
      });
    },
  });

  const columns: Column<BrokerSummaryDto>[] = useMemo(() => {
    const starCol: Column<BrokerSummaryDto> = {
      key: "star",
      header: "",
      className: "w-8",
      render: (b) => (
        <StarButton
          entityType="Broker"
          entityId={b.id}
          entityTitle={b.companyName}
          isWatching={watchStatuses.data?.[b.id] ?? false}
        />
      ),
    };
    const nameCol: Column<BrokerSummaryDto> = {
      key: "name",
      header: "Broker",
      render: (b) => (
        <Link to={`/brokers/${b.id}`} className="font-semibold hover:text-gold-shadow">
          {b.companyName}
          {b.dba && <span className="text-caption text-gray-mid"> — DBA {b.dba}</span>}
          <div className="text-caption text-gray-mid font-normal">{b.name ?? "—"}</div>
        </Link>
      ),
    };
    const stateCol: Column<BrokerSummaryDto> = {
      key: "state", header: "State", render: (b) => b.state ?? "—",
    };
    const statusCol: Column<BrokerSummaryDto> = {
      key: "status", header: "Status",
      render: (b) => <Badge status={STATUS_BADGE[b.status]}>{b.status}</Badge>,
    };

    if (tableTab === "archive") {
      return [
        starCol, nameCol, stateCol, statusCol,
        {
          key: "reason",
          header: "Archive reason",
          render: (b) => (
            <span className="text-caption">
              {b.disqualificationReason || b.archiveReason || "—"}
            </span>
          ),
        },
        {
          key: "archived",
          header: "Archived",
          render: (b) => (
            <div className="text-caption">
              <div>{b.archivedDate ? date(b.archivedDate) : "—"}</div>
              <div className="font-mono text-gray-mid">{b.archivedByUserId?.slice(0, 8) ?? ""}</div>
            </div>
          ),
        },
        {
          key: "actions",
          header: "",
          className: "w-10 text-right",
          render: (b) => (
            canUpdate
              ? <Link to={`/brokers/${b.id}?action=reactivate`}>
                  <span className="text-caption text-gold-shadow font-display uppercase tracking-wide hover:underline">
                    Reactivate
                  </span>
                </Link>
              : null
          ),
        },
      ];
    }

    return [
      starCol, nameCol, stateCol, statusCol,
      {
        // Lead Conversion Router (WS-3) — the broker's immutable payee posture.
        key: "type",
        header: "Type",
        render: (b) =>
          b.brokerType === "Junior" ? (
            <Badge status="gold">Junior</Badge>
          ) : (
            <span className="text-caption text-gray-mid">Independent</span>
          ),
      },
      {
        // WS6 — graduated brokers stay visible in the roster table; the Team
        // column is where their team membership shows.
        key: "team",
        header: "Team",
        render: (b) =>
          b.teamId ? (
            <span className="inline-flex items-center gap-space-1">
              <span className="text-caption">{b.teamName ?? "—"}</span>
              <Badge status="gold">Graduated</Badge>
            </span>
          ) : (
            <span className="text-caption text-gray-mid">—</span>
          ),
      },
      { key: "source", header: "Source", render: (b) => <span className="text-caption">{b.source}</span> },
      {
        key: "assigned",
        header: "Assigned",
        render: (b) => (
          <span className="font-mono text-caption text-gray-mid">
            {b.assignedC1LiaisonUserId?.slice(0, 8) ?? "—"}
          </span>
        ),
      },
      {
        key: "eo",
        header: "E&O",
        render: (b) => (
          <ExpirationCell alerting={b.hasEoAlert} days={daysUntilDate(b.eoExpirationDate)} />
        ),
      },
      {
        key: "license",
        header: "License",
        render: (b) => (
          <ExpirationCell alerting={b.hasLicenseAlert} days={daysUntilDate(b.licenseExpirationDate)} />
        ),
      },
      {
        key: "actions",
        header: "",
        className: "w-10 text-right",
        render: (b) => (
          <RowActions
            actions={buildRowActions(b, {
              canUpdate,
              canQualify,
              onLogActivity: () => setShowLogActivity(b),
            })}
          />
        ),
      },
    ];
  }, [canQualify, canUpdate, tableTab, tz, watchStatuses.data]);

  const allAlerts = alerts.data ?? [];
  const totalInTable = table.data?.data.length ?? 0;
  const totalPages = useMemo(() => {
    const data = table.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [table.data]);

  const bulkActions: BulkAction[] = [
    {
      key: "advance-qualified",
      label: "Advance → Qualified",
      icon: <ArrowRight size={14} strokeWidth={1.5} aria-hidden />,
      primary: true,
      disabled: !canQualify,
      onClick: async (ids) => {
        const ok = await dialogs.confirm({
          title: "Advance brokers?",
          message: `Advance ${ids.length} broker${ids.length === 1 ? "" : "s"} to Qualified?`,
          confirmLabel: "Advance",
        });
        if (!ok) return;
        bulkStatus.mutate({ ids, status: "Qualified" });
      },
    },
    {
      key: "suspend",
      label: "Suspend",
      destructive: true,
      disabled: !canQualify,
      onClick: async (ids) => {
        const ok = await dialogs.confirm({
          title: "Suspend brokers?",
          message: `Suspend ${ids.length} broker${ids.length === 1 ? "" : "s"}?`,
          confirmLabel: "Suspend",
          destructive: true,
        });
        if (!ok) return;
        bulkStatus.mutate({ ids, status: "Suspended" });
      },
    },
    {
      key: "export",
      label: "Export CSV",
      icon: <Download size={14} strokeWidth={1.5} aria-hidden />,
      onClick: (ids) => {
        const rows = (table.data?.data ?? []).filter((r) => ids.includes(r.id));
        downloadCsv(rows, tz);
      },
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Partners"
        title="Brokers"
        actions={
          <div className="flex items-center gap-space-3">
            <div className="inline-flex rounded-md border border-border-light bg-white-pure overflow-hidden">
              <button
                type="button"
                onClick={() => setView("kanban")}
                className={cn(
                  "h-8 px-space-3 inline-flex items-center gap-space-1 text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
                  view === "kanban" ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
                )}
              >
                <Grid2X2 size={14} strokeWidth={1.5} aria-hidden /> Kanban
              </button>
              <button
                type="button"
                onClick={() => setView("table")}
                className={cn(
                  "h-8 px-space-3 inline-flex items-center gap-space-1 text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
                  view === "table" ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
                )}
              >
                <List size={14} strokeWidth={1.5} aria-hidden /> Table
              </button>
            </div>
            {canCreate && (
              <Link to="/brokers/new">
                <Button size="sm"><Plus size={14} strokeWidth={1.5} aria-hidden /> New broker</Button>
              </Link>
            )}
          </div>
        }
      />

      {allAlerts.length > 0 && (
        <div className="border-l-[3px] border-status-warning bg-status-warning/10 p-space-3 mb-space-4 rounded-md">
          <div className="flex items-center gap-space-2 font-display font-semibold text-caption uppercase tracking-wide text-status-warning">
            <AlertTriangle size={14} strokeWidth={1.5} aria-hidden />
            {allAlerts.length} expiration alert{allAlerts.length === 1 ? "" : "s"}
          </div>
          <div className="mt-space-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-space-2">
            {allAlerts.slice(0, 6).map((a) => (
              <Link
                key={`${a.brokerId}-${a.alertType}`}
                to={`/brokers/${a.brokerId}`}
                className="text-small hover:underline"
              >
                <span className="font-semibold">{a.companyName}</span>
                <span className="ml-space-2 font-mono text-caption">
                  {a.alertType === "EoInsurance" ? "E&O" : "License"} · {a.daysUntilExpiry < 0 ? "expired" : `${a.daysUntilExpiry}d`}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-space-3">
        {/* My Brokers / All toggle + search sit above the Active Pipeline /
            Archived tab. The toggle shows on both views; the search box and
            the alert filter are table-only. */}
        <div className="flex flex-wrap items-center gap-space-3">
          {/* forceVisible: Brokers.Read is NOT row-scoped, so C0/C1 (the
              roles hidden by the toggle's default rule) need the control
              here — the "My brokers doesn't filter" fix. */}
          <MyToggle pageKey={PAGE_KEY} label="My brokers" onChange={handleMine} forceVisible />
          {view === "table" && (
            <>
              <Input
                type="search"
                placeholder="Search companies, contacts…"
                value={state.search}
                onChange={(e) => setState((s) => ({ ...s, search: e.target.value }))}
                className="max-w-sm"
              />
              <label className="flex items-center gap-space-2 text-caption">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-gold"
                  checked={state.onlyAlerts}
                  onChange={(e) => setState((s) => ({ ...s, onlyAlerts: e.target.checked }))}
                />
                Only with expiration alerts
              </label>
            </>
          )}
        </div>

        {view === "kanban" ? (
          state.assignedToMe &&
          !pipeline.isLoading &&
          pipeline.data &&
          Object.values(pipeline.data.stages).every((list) => list.length === 0) ? (
            <MyBrokersEmptyState />
          ) : (
            <KanbanView
              stages={pipeline.data?.stages}
              loading={pipeline.isLoading}
              watchStatuses={watchStatuses.data}
              canUpdate={canUpdate}
              onGraduate={setGraduating}
            />
          )
        ) : (
          <>
            {/* Broker Status Refactor §8 — Active Pipeline / Archived tabs */}
            <div className="inline-flex rounded-md border border-border-light bg-white-pure overflow-hidden w-max">
              <button
                type="button"
                onClick={() => setTableTab("pipeline")}
                className={cn(
                  "h-8 px-space-4 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
                  tableTab === "pipeline" ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10",
                )}
              >
                Active pipeline
              </button>
              <button
                type="button"
                onClick={() => setTableTab("archive")}
                className={cn(
                  "h-8 px-space-4 inline-flex items-center text-label font-display font-bold uppercase tracking-button border-l border-border-light transition-colors duration-fast ease-standard",
                  tableTab === "archive" ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10",
                )}
              >
                Archived
              </button>
            </div>

            <SavedFilterBar<BrokerTableState>
              pageKey={PAGE_KEY}
              currentState={state}
              onApply={(next) => setState({ ...DEFAULT_STATE, ...next })}
            />

            <BulkActionBar
              selectedIds={selectedIds}
              totalRows={totalInTable}
              onClear={() => setSelectedIds([])}
              actions={bulkActions}
            />

            {state.assignedToMe &&
            !state.search &&
            !state.onlyAlerts &&
            !table.isLoading &&
            !table.isError &&
            (table.data?.totalCount ?? 0) === 0 ? (
              <MyBrokersEmptyState />
            ) : (
              <DataTable
                columns={columns}
                rows={table.data?.data ?? []}
                loading={table.isLoading}
                error={table.isError ? (table.error as Error).message : null}
                emptyMessage="No brokers match your filters."
                rowKey={(b) => b.id}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              />
            )}

            {table.data && table.data.totalCount > 0 && (
              <Pagination
                page={table.data.page}
                pageSize={table.data.pageSize}
                totalCount={table.data.totalCount}
                totalPages={totalPages}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            )}
          </>
        )}
      </div>

      {showLogActivity && (
        <LogActivityHint broker={showLogActivity} onClose={() => setShowLogActivity(null)} />
      )}

      {graduating && (
        <AssignToTeamModal broker={graduating} onClose={() => setGraduating(null)} />
      )}
    </>
  );
}

/** Teaching empty state for the "My brokers" scope (WS2): a C1 with no
 * assignments should learn how brokers get assigned, not stare at a blank
 * table/board. */
function MyBrokersEmptyState() {
  return (
    <EmptyState
      icon={UserCheck}
      title="No brokers are assigned to you yet"
      description="Brokers are assigned a liaison automatically when a lead you own converts, or manually from a broker's detail page. Switch the toggle above to All to browse the full roster."
    />
  );
}

function buildRowActions(
  b: BrokerSummaryDto,
  perm: { canUpdate: boolean; canQualify: boolean; onLogActivity: () => void }
): RowAction[] {
  return [
    {
      key: "view",
      label: "View",
      icon: Eye,
      href: `/brokers/${b.id}`,
    },
    {
      key: "log-activity",
      label: "Log activity",
      icon: NotebookPen,
      hidden: !perm.canUpdate,
      onClick: perm.onLogActivity,
    },
    {
      key: "upload-doc",
      label: "Upload document",
      icon: UploadIcon,
      hidden: !perm.canUpdate,
      href: `/brokers/${b.id}?tab=documents`,
    },
    {
      key: "advance",
      label: b.status === "Certified" ? "Manage status" : "Advance status",
      icon: UserCheck,
      hidden: !perm.canQualify || b.status === "Inactive" || b.status === "Suspended",
      href: `/brokers/${b.id}?action=status`,
    },
  ];
}

function LogActivityHint({ broker, onClose }: { broker: BrokerSummaryDto; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Log activity for ${broker.companyName}`}
      className="fixed inset-0 z-40 bg-onyx/50 flex items-center justify-center p-space-4"
      onClick={onClose}
    >
      <div
        className="bg-white-pure rounded-lg shadow-lg max-w-md w-full p-space-5 border-t-[2px] border-t-gold"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
          Log activity
        </div>
        <h2 className="font-display font-bold text-h3 text-onyx m-0 mb-space-3 normal-case tracking-normal">
          {broker.companyName}
        </h2>
        <p className="text-small text-gray-mid mb-space-4">
          Activity logging opens inline from the broker detail page. For now, open the broker to add a
          call, email, or meeting log.
        </p>
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose}>
            Close
          </Button>
          <Link to={`/brokers/${broker.id}?tab=activity`} onClick={onClose}>
            <Button variant="primary" size="sm">
              Open broker
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function downloadCsv(rows: BrokerSummaryDto[], tz: string) {
  const header = ["Company", "DBA", "Contact", "Status", "Source", "State", "E&O expires", "License expires"];
  const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const lines = [header.map(escape).join(",")];
  for (const b of rows) {
    lines.push(
      [
        b.companyName,
        b.dba ?? "",
        b.name ?? "",
        b.status,
        b.source,
        b.state ?? "",
        b.eoExpirationDate ?? "",
        b.licenseExpirationDate ?? "",
      ]
        .map((v) => escape(String(v)))
        .join(",")
    );
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `brokers-${isoDateInTz(new Date(), tz)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** WS6 — compact 4-dot pipeline-stage indicator for kanban cards. Dots up
 *  to (and including) the current stage are filled. */
function StageDots({ stage }: { stage: BrokerStatus }) {
  const idx = PIPELINE_ORDER.indexOf(stage);
  if (idx < 0) return null;
  return (
    <span
      role="img"
      aria-label={`Stage ${idx + 1} of ${PIPELINE_ORDER.length}: ${stage}`}
      className="inline-flex items-center gap-space-1"
    >
      {PIPELINE_ORDER.map((s, i) => (
        <span
          key={s}
          aria-hidden
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            i <= idx ? "bg-gold" : "bg-border-light",
          )}
        />
      ))}
    </span>
  );
}

function KanbanView({
  stages,
  loading,
  watchStatuses,
  canUpdate,
  onGraduate,
}: {
  stages?: Record<BrokerStatus, BrokerSummaryDto[]>;
  loading: boolean;
  watchStatuses?: Record<string, boolean>;
  canUpdate: boolean;
  onGraduate: (broker: BrokerSummaryDto) => void;
}) {
  if (loading) {
    return <div className="text-caption uppercase tracking-wide text-gray-mid">Loading pipeline…</div>;
  }
  if (!stages) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-space-3">
      {PIPELINE_ORDER.map((stage) => {
        const items = stages[stage] ?? [];
        return (
          <div key={stage} className="bg-white-pure border border-border-light rounded-lg flex flex-col min-h-[12rem]">
            <div className="px-space-3 py-space-2 border-b border-border-light flex items-center justify-between">
              <Badge status={STATUS_BADGE[stage]}>{stage}</Badge>
              <span className="text-caption text-gray-mid">{items.length}</span>
            </div>
            <div className="p-space-2 space-y-space-2">
              {items.length === 0 ? (
                <div className="text-caption text-gray-mid italic">No brokers</div>
              ) : (
                items.map((b) => (
                  <Link
                    key={b.id}
                    to={`/brokers/${b.id}`}
                    className="block border border-border-light rounded-md p-space-2 hover:border-gold hover:shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-space-2">
                      <div className="font-semibold text-small text-onyx truncate">{b.companyName}</div>
                      <StarButton
                        entityType="Broker"
                        entityId={b.id}
                        entityTitle={b.companyName}
                        isWatching={watchStatuses?.[b.id] ?? false}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-space-2">
                      <div className="text-caption text-gray-mid truncate">
                        {b.name ?? "—"} · {b.state ?? "—"}
                      </div>
                      <StageDots stage={b.status} />
                    </div>
                    {(b.isNewApplicant || b.hasEoAlert || b.hasLicenseAlert ||
                      (stage === "Certified" && b.readyToGraduate)) && (
                      <div className="mt-space-1 flex flex-wrap items-center gap-space-1">
                        {b.isNewApplicant && <Badge status="gold">New applicant</Badge>}
                        {(b.hasEoAlert || b.hasLicenseAlert) && (
                          <ExpirationBadge days={b.alertDaysUntilExpiry} />
                        )}
                        {stage === "Certified" && b.readyToGraduate && (
                          canUpdate ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onGraduate(b);
                              }}
                              className="inline-flex items-center rounded-full border border-gold bg-gold-highlight/10 px-space-2 py-0.5 text-caption font-display font-semibold uppercase tracking-wide text-gold-shadow hover:bg-gold-highlight/20 transition-colors duration-fast ease-standard"
                              data-testid={`graduate-${b.id}`}
                            >
                              Ready to graduate
                            </button>
                          ) : (
                            <Badge status="gold">Ready to graduate</Badge>
                          )
                        )}
                      </div>
                    )}
                  </Link>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
